import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useAuth } from 'react-oidc-context';
import PatientAutocomplete from '../common/PatientAutocomplete';
import DoctorAutocomplete from '../common/DoctorAutocomplete';
import ToastContainer from '../common/ToastContainer';
import { useToast } from '../../hooks/useToast';
import { useOrganizationTimezone } from '../../hooks/useOrganizationTimezone';
import ProviderService from '../../services/providerService';
import PatientService from '../../services/patientService';
import LaboratoryOrderService from '../../services/laboratoryOrderService';
import LaboratoryOrderPayments from './LaboratoryOrderPayments';
import {
  STATUS_LABELS,
  formatLaboratoryDateTime
} from './laboratoryOrderUtils';
import './LaboratoryOrderForm.css';

const LaboratoryOrderForm = () => {
  const { orderId } = useParams();
  const isEdit = useMemo(() => Boolean(orderId), [orderId]);
  const navigate = useNavigate();
  const location = useLocation();
  // Set when the form is opened from a patient's laboratory section, so the
  // patient comes preselected and Regresar goes back to their filtered list.
  const presetPatientId = useMemo(
    () => new URLSearchParams(location.search).get('patientId') || '',
    [location.search]
  );
  const auth = useAuth();
  const token = auth.user?.access_token;
  const { timezone } = useOrganizationTimezone();
  const { toasts, removeToast, success: showSuccess, error: showError } = useToast();

  const [providers, setProviders] = useState([]);
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [selectedDoctor, setSelectedDoctor] = useState(null);
  const [selectedProviderId, setSelectedProviderId] = useState('');
  const [workType, setWorkType] = useState('');
  const [requestedDeliveryDate, setRequestedDeliveryDate] = useState('');
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [existingOrder, setExistingOrder] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [downloadingAttachmentId, setDownloadingAttachmentId] = useState(null);

  const uploadFilesToOrder = useCallback(async (targetOrderId, filesToUpload) => {
    if (!filesToUpload.length || !token) {
      return;
    }

    for (const file of filesToUpload) {
      const contentType = file.type || 'application/octet-stream';
      const uploadData = await LaboratoryOrderService.getAttachmentUploadUrl(
        targetOrderId,
        { fileName: file.name, contentType },
        token
      );
      await LaboratoryOrderService.uploadAttachmentFile(uploadData.uploadUrl, file, contentType);
      await LaboratoryOrderService.attachFile(
        targetOrderId,
        {
          attachmentId: uploadData.attachmentId,
          fileName: file.name,
          contentType,
          key: uploadData.key
        },
        token
      );
    }
  }, [token]);

  const loadData = useCallback(async () => {
    if (!token) {
      return;
    }

    try {
      setIsLoading(true);
      const [providersResponse, order, presetPatient] = await Promise.all([
        ProviderService.getAllProviders(token),
        isEdit ? LaboratoryOrderService.getOrderById(orderId, token) : Promise.resolve(null),
        !isEdit && presetPatientId
          ? PatientService.getPatientById(presetPatientId, token).catch(() => null)
          : Promise.resolve(null)
      ]);

      const providerItems = [...(providersResponse.data || [])].sort((left, right) => {
        return (left.name || '').localeCompare(right.name || '', 'es');
      });
      setProviders(providerItems);

      if (order) {
        setExistingOrder(order);
        setSelectedPatient({
          id: order.patientId,
          firstName: order.patientName,
          lastName: ''
        });
        setSelectedDoctor({
          id: order.doctorId,
          name: order.doctorName
        });
        setSelectedProviderId(order.providerId || '');
        setWorkType(order.workType || '');
        setRequestedDeliveryDate(order.requestedDeliveryDate || '');
      } else if (presetPatient) {
        setSelectedPatient(presetPatient);
      }
    } catch (error) {
      showError(isEdit ? 'No se pudo cargar la orden de laboratorio' : 'No se pudo preparar el formulario');
    } finally {
      setIsLoading(false);
    }
  }, [isEdit, orderId, presetPatientId, showError, token]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleBack = () => {
    navigate(presetPatientId
      ? `/laboratory-orders?patientId=${presetPatientId}`
      : '/laboratory-orders');
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!selectedPatient || !selectedDoctor || !selectedProviderId || !workType.trim() || !requestedDeliveryDate) {
      showError('Completa paciente, odontologo, proveedor, tipo de trabajo y fecha de entrega');
      return;
    }

    const selectedProvider = providers.find((provider) => provider.id === selectedProviderId);
    if (!selectedProvider) {
      showError('Selecciona un proveedor valido');
      return;
    }

    const payload = {
      patientId: selectedPatient.id,
      patientName: `${selectedPatient.firstName || ''} ${selectedPatient.lastName || ''}`.trim(),
      doctorId: selectedDoctor.id,
      doctorName: selectedDoctor.name,
      providerId: selectedProvider.id,
      providerName: selectedProvider.name,
      workType: workType.trim(),
      requestedDeliveryDate
    };

    try {
      setIsSubmitting(true);
      const savedOrder = isEdit
        ? await LaboratoryOrderService.updateOrder(orderId, payload, token)
        : await LaboratoryOrderService.createOrder(payload, token);

      if (selectedFiles.length > 0) {
        setIsUploading(true);
        await uploadFilesToOrder(savedOrder.id, selectedFiles);
      }

      navigate(presetPatientId
        ? `/laboratory-orders?patientId=${presetPatientId}`
        : '/laboratory-orders', {
        state: {
          message: isEdit ? 'Orden de laboratorio actualizada' : 'Orden de laboratorio creada',
          messageId: `${savedOrder.id}-${Date.now()}`
        }
      });
    } catch (error) {
      showError(error.message || (isEdit ? 'No se pudo actualizar la orden' : 'No se pudo crear la orden'));
    } finally {
      setIsSubmitting(false);
      setIsUploading(false);
    }
  };

  const handleUploadExtraFiles = async (event) => {
    const filesToUpload = Array.from(event.target.files || []);
    event.target.value = '';

    if (!filesToUpload.length || !existingOrder) {
      return;
    }

    try {
      setIsUploading(true);
      await uploadFilesToOrder(existingOrder.id, filesToUpload);
      await loadData();
      showSuccess('Adjuntos cargados correctamente');
    } catch (error) {
      showError(error.message || 'No se pudieron cargar los adjuntos');
    } finally {
      setIsUploading(false);
    }
  };

  const handleDeleteAttachment = async (attachment) => {
    if (!existingOrder || !attachment?.id) {
      return;
    }

    const confirmed = window.confirm(`¿Eliminar el adjunto ${attachment.fileName}?`);
    if (!confirmed) {
      return;
    }

    try {
      setIsUploading(true);
      await LaboratoryOrderService.deleteAttachment(existingOrder.id, attachment.id, token);
      await loadData();
      showSuccess('Adjunto eliminado correctamente');
    } catch (error) {
      showError(error.message || 'No se pudo eliminar el adjunto');
    } finally {
      setIsUploading(false);
    }
  };

  const handlePaymentsChanged = (updatedOrder) => {
    if (!updatedOrder) {
      return;
    }
    setExistingOrder(updatedOrder);
    const message = updatedOrder.payments && updatedOrder.payments.length
      ? 'Pagos actualizados'
      : 'Pago actualizado';
    showSuccess(message);
  };

  const handleDownloadAttachment = async (attachment) => {
    if (!attachment?.viewUrl) {
      showError('No se pudo preparar la descarga del adjunto');
      return;
    }

    try {
      setDownloadingAttachmentId(attachment.id);
      const response = await fetch(attachment.viewUrl);

      if (!response.ok) {
        throw new Error('No se pudo descargar el adjunto');
      }

      const blob = await response.blob();
      const objectUrl = window.URL.createObjectURL(blob);
      const downloadLink = document.createElement('a');
      downloadLink.href = objectUrl;
      downloadLink.download = attachment.fileName || 'adjunto';
      document.body.appendChild(downloadLink);
      downloadLink.click();
      downloadLink.remove();
      window.URL.revokeObjectURL(objectUrl);
    } catch (error) {
      showError(error.message || 'No se pudo descargar el adjunto');
    } finally {
      setDownloadingAttachmentId(null);
    }
  };

  if (isLoading) {
    return (
      <div className="laboratory-order-form-container loading">
        <div className="loading-spinner"></div>
      </div>
    );
  }

  return (
    <div className="laboratory-order-form-container">
      <div className="laboratory-order-form-header">
        <div>
          <h1>{isEdit ? 'Editar Orden de Laboratorio' : 'Nueva Orden de Laboratorio'}</h1>
          <p>{isEdit ? 'Actualiza datos y adjuntos de la orden.' : 'Captura la orden completa en una pantalla dedicada.'}</p>
        </div>
        <button className="btn-secondary" onClick={handleBack}>
          Volver
        </button>
      </div>

      <form onSubmit={handleSubmit} className="laboratory-order-form-card">
        <div className="laboratory-form-grid">
          <div className="form-group full-width">
            <label>Paciente <span className="required">*</span></label>
            <PatientAutocomplete
              selectedPatient={selectedPatient}
              onPatientSelect={setSelectedPatient}
              placeholder="Selecciona un paciente"
              searchPlaceholder="Buscar paciente..."
              className="laboratory-form-autocomplete"
            />
          </div>

          <div className="form-group form-group-doctor">
            <label>Odontologo tratante <span className="required">*</span></label>
            <DoctorAutocomplete
              selectedDoctor={selectedDoctor}
              onDoctorSelect={setSelectedDoctor}
              placeholder="Selecciona un odontologo"
              searchPlaceholder="Buscar odontologo..."
              className="laboratory-form-autocomplete"
            />
          </div>

          <div className="form-group form-group-provider">
            <label htmlFor="providerId">Proveedor <span className="required">*</span></label>
            <select
              id="providerId"
              value={selectedProviderId}
              onChange={(event) => setSelectedProviderId(event.target.value)}
            >
              <option value="">Selecciona un proveedor</option>
              {providers.map((provider) => (
                <option key={provider.id} value={provider.id}>
                  {provider.name}
                </option>
              ))}
            </select>
          </div>

          <div className="form-group form-group-delivery-date">
            <label htmlFor="requestedDeliveryDate">Fecha de entrega solicitada <span className="required">*</span></label>
            <input
              id="requestedDeliveryDate"
              type="date"
              value={requestedDeliveryDate}
              onChange={(event) => setRequestedDeliveryDate(event.target.value)}
            />
          </div>

          <div className="form-group full-width">
            <label htmlFor="workType">Tipo de trabajo <span className="required">*</span></label>
            <textarea
              id="workType"
              rows="5"
              value={workType}
              onChange={(event) => setWorkType(event.target.value)}
              placeholder="Ej. Corona de zirconia unidad 11, tono A2"
            />
          </div>

          <div className="form-group full-width">
            <label htmlFor="attachments">Adjuntos {isEdit ? 'nuevos' : ''}</label>
            <input
              id="attachments"
              type="file"
              multiple
              onChange={(event) => setSelectedFiles(Array.from(event.target.files || []))}
            />
            <div className="selected-files-list">
              {selectedFiles.map((file) => (
                <span key={`${file.name}-${file.lastModified}`} className="file-chip">
                  {file.name}
                </span>
              ))}
            </div>
          </div>
        </div>

        <div className="form-actions">
          <button type="button" className="btn-secondary" onClick={handleBack}>
            Cancelar
          </button>
          <button
            type="submit"
            className="btn-primary"
            disabled={isSubmitting || isUploading}
            aria-label={isSubmitting || isUploading ? 'Guardando orden' : 'Guardar orden'}
          >
            {isSubmitting || isUploading ? <span className="button-spinner" aria-hidden="true"></span> : 'Guardar'}
          </button>
        </div>
      </form>

      {isEdit && existingOrder && (
        <div className="laboratory-order-side-sections">
          <section className="laboratory-order-info-card">
            <div className="section-header-row">
              <div>
                <h2>Adjuntos actuales</h2>
                <p>Agrega archivos adicionales sin salir de esta pantalla.</p>
              </div>
              <div>
                <input
                  id="extra-order-upload"
                  type="file"
                  multiple
                  onChange={handleUploadExtraFiles}
                  style={{ display: 'none' }}
                />
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={() => document.getElementById('extra-order-upload')?.click()}
                  disabled={isUploading}
                  aria-label={isUploading ? 'Subiendo adjuntos' : 'Agregar adjuntos'}
                >
                  {isUploading ? <span className="button-spinner" aria-hidden="true"></span> : 'Agregar adjuntos'}
                </button>
              </div>
            </div>

            {existingOrder.attachments?.length ? (
              <div className="attachment-list">
                {existingOrder.attachments.map((attachment) => (
                  <div key={attachment.id} className="attachment-item">
                    <div className="attachment-meta">
                      <span className="attachment-name">{attachment.fileName}</span>
                      <span className="attachment-date">{formatLaboratoryDateTime(attachment.uploadedAt, timezone)}</span>
                    </div>
                    <div className="attachment-actions">
                      <button
                        type="button"
                        className="attachment-action-link download"
                        onClick={() => handleDownloadAttachment(attachment)}
                        disabled={downloadingAttachmentId === attachment.id || isUploading}
                      >
                        <i
                          className={`fas ${downloadingAttachmentId === attachment.id ? 'fa-spinner fa-spin' : 'fa-download'}`}
                          aria-hidden="true"
                        ></i>
                        Descargar
                      </button>
                      <button
                        type="button"
                        className="attachment-action-link delete"
                        onClick={() => handleDeleteAttachment(attachment)}
                        disabled={isUploading || downloadingAttachmentId === attachment.id}
                      >
                        <i className={`fas ${isUploading ? 'fa-spinner fa-spin' : 'fa-trash'}`} aria-hidden="true"></i>
                        Eliminar
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="muted-copy">Sin adjuntos</p>
            )}
          </section>

          <LaboratoryOrderPayments
            order={existingOrder}
            token={token}
            onPaymentsChanged={handlePaymentsChanged}
          />

          <section className="laboratory-order-info-card">
            <h2>Historial</h2>
            <div className="history-list">
              {existingOrder.statusHistory?.map((entry) => (
                <div key={entry.id} className="history-item">
                  <div className="history-status-row">
                    <span className="history-status">{STATUS_LABELS[entry.status] || entry.status}</span>
                    <span className="history-date">{formatLaboratoryDateTime(entry.changedAt, timezone)}</span>
                  </div>
                  {entry.fromStatus && (
                    <span className="history-meta">Desde {STATUS_LABELS[entry.fromStatus] || entry.fromStatus}</span>
                  )}
                  {entry.note && <span className="history-meta">{entry.note}</span>}
                </div>
              ))}
            </div>
          </section>
        </div>
      )}

      <ToastContainer toasts={toasts} removeToast={removeToast} />
    </div>
  );
};

export default LaboratoryOrderForm;